import { useNavigate, useSearchParams } from 'react-router-dom';
import { Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { memo, useMemo, useRef, useEffect, Suspense } from 'react';
import { useCurrentWorkspace } from '@hooks/workspace';
import { ApprovalWorkflowItemDto, AWAssetDto, AWStageDto } from '@api/Api';
import ApprovalDropDownMenu from '@components/EntitiesDropdownMenu/ApprovalDropDownMenu';
import { ReactComponent as MenuSecondarySvg } from '@assets/icons/menu-secondary.svg';

import CreatedCell from './components/CreatedCell';
import ManagersCell from './components/ManagersCell';
import NameCell from './components/NameCell';
import ProgressCell from './components/ProgressCell';
import AssetsCell from './components/AssetsCell';
import DeadlineCell from './components/DeadlineCell';

import './ApprovalsTable.less';

type ApprovalsTableProps = {
  refetchApprovalsData: () => void;
  isShowCompanyName?: boolean;
  approvals: ApprovalWorkflowItemDto[];
};

type ApprovalsTableItem =
  | ApprovalWorkflowItemDto
  | { type: string; campaignName: string; id: string };

export default memo(function ApprovalsTable({
  approvals,
  isShowCompanyName,
  refetchApprovalsData
}: ApprovalsTableProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentWorkspace] = useCurrentWorkspace(false);
  const searchParamsRef = useRef(searchParams);
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  useEffect(() => {
    if (!approvals) return;
    const id = searchParamsRef.current.get('approvalId');
    if (!id) return;
    const timeoutId = setTimeout(() => {
      document
        .querySelector(`[data-row-key="${id}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      searchParamsRef.current.delete('approvalId');
      setSearchParamsRef.current(searchParamsRef.current, { replace: true });
    }, 1000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [approvals]);

  const columns: ColumnsType<ApprovalsTableItem> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        width: '25%',
        render: (name: string, record: ApprovalsTableItem) =>
          'type' in record ? null : <NameCell name={name} />
      },
      {
        title: 'Status',
        dataIndex: 'stages',
        key: 'stages',
        width: '19%',
        render: (stages: AWStageDto[], record: ApprovalsTableItem) =>
          'type' in record ? null : <ProgressCell approvalWorkflow={record} />
      },
      {
        title: 'Deadline',
        dataIndex: 'stages',
        key: 'stages',
        width: '17%',
        render: (stages: AWStageDto[], record: ApprovalsTableItem) =>
          'type' in record ? null : (
            <DeadlineCell
              stages={stages}
              currentStageId={record.currentStageId}
              isFinished={record.isFinished}
              isPaused={record.isPaused}
            />
          )
      },
      {
        title: 'Media',
        dataIndex: 'assets',
        key: 'assets',
        width: '13%',
        render: (assets: AWAssetDto[]) => <AssetsCell assets={assets} />
      },
      {
        title: 'Managers',
        dataIndex: 'stages',
        key: 'stages',
        width: '15%',
        render: (stages: AWStageDto[], record: ApprovalsTableItem) =>
          'type' in record ? null : <ManagersCell stages={stages} />
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: '12%',
        render: (createdAt: string) => <CreatedCell createdAt={createdAt} />
      },
      {
        title: '',
        dataIndex: 'id',
        key: 'id',
        width: '4%',
        render: (_: any, record: ApprovalsTableItem) => {
          if ('type' in record) return null;
          return (
            <div className="action-cell">
              <Suspense fallback={null}>
                <ApprovalDropDownMenu
                  approval={record}
                  refetchApprovalsData={refetchApprovalsData}
                />
              </Suspense>
            </div>
          );
        }
      }
    ],
    []
  );

  const tableData: ApprovalsTableItem[] = useMemo(() => {
    if (!isShowCompanyName) return approvals;

    const result: ApprovalsTableItem[] = [];
    let lastCampaign: string | null = null;
    approvals.forEach((item) => {
      if (item.campaignName !== lastCampaign) {
        result.push({
          type: 'group',
          campaignName: item.campaignName,
          id: `group-${item.campaignName}-${item.id}`
        });
        lastCampaign = item.campaignName;
      }
      result.push(item);
    });
    return result;
  }, [approvals, isShowCompanyName]);

  function CustomRow(props: any) {
    const record: any = props.children?.[0]?.props?.record;
    if (record.type === 'group') {
      return (
        <tr key={record?.id} className="row-group">
          <td colSpan={columns.length}>
            <div className="row-group-name">
              <MenuSecondarySvg />
              {record.campaignName}
            </div>
          </td>
        </tr>
      );
    }
    return <tr key={record?.id} {...props} />;
  }

  return (
    <div className="approvals-table-wrapper">
      <Table
        className="approvals-table"
        columns={columns}
        dataSource={tableData}
        components={{
          body: {
            row: CustomRow
          }
        }}
        onRow={(record: ApprovalsTableItem) => {
          if ('type' in record) return {};
          return {
            onClick: () => {
              if (!currentWorkspace?.id) return;
              navigate(
                `/campaigns/approvals/${currentWorkspace.id}/${record?.campaignId}/${record.id}`
              );
            }
          };
        }}
        rowKey={(record) => record.id}
        tableLayout="fixed"
        pagination={false}
      />
    </div>
  );
});
